import styled from 'styled-components';

import { Permission, moduleLabel } from '../../../../types/permission';

interface Props {
  module: string;
  permissions: Permission[];
  selected: Set<string>;
  onChange: (next: Set<string>) => void;
  disabled?: boolean;
}

const Card = styled.fieldset`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  padding: 14px 16px;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
  background: ${({ theme }) => theme.colors.bgSurface};
`;

const Header = styled.legend`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 0 4px;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.textMuted};
`;

const Counter = styled.span`
  font-size: 11px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 999px;
  background: ${({ theme }) => theme.colors.bgMuted};
  color: ${({ theme }) => theme.colors.textSecondary};
  letter-spacing: 0;
  text-transform: none;
`;

const Toolbar = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  font-size: 12px;
`;

const ToolbarBtn = styled.button`
  background: transparent;
  border: none;
  padding: 2px 6px;
  color: ${({ theme }) => theme.colors.primary};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  border-radius: ${({ theme }) => theme.radius.sm};

  &:hover:not(:disabled) {
    background: ${({ theme }) => theme.colors.bgMuted};
  }

  &:disabled {
    color: ${({ theme }) => theme.colors.textMuted};
    cursor: not-allowed;
  }
`;

const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Row = styled.label`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 6px;
  border-radius: ${({ theme }) => theme.radius.sm};
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.bgMuted};
  }

  input {
    margin-top: 3px;
  }
`;

const RowText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 2px;
  line-height: 1.3;

  strong {
    font-size: 13.5px;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.textPrimary};
  }

  small {
    font-size: 12.5px;
    color: ${({ theme }) => theme.colors.textMuted};
  }
`;

export const PermissionGroup = ({
  module,
  permissions,
  selected,
  onChange,
  disabled,
}: Props) => {
  const total = permissions.length;
  const selectedHere = permissions.filter((p) => selected.has(p.key)).length;

  const toggle = (key: string) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    onChange(next);
  };

  const markAll = () => {
    const next = new Set(selected);
    permissions.forEach((p) => next.add(p.key));
    onChange(next);
  };

  const clearAll = () => {
    const next = new Set(selected);
    permissions.forEach((p) => next.delete(p.key));
    onChange(next);
  };

  return (
    <Card>
      <Header>
        {moduleLabel(module)}
        <Counter aria-live="polite">
          {selectedHere}/{total}
        </Counter>
      </Header>
      {!disabled && (
        <Toolbar>
          <ToolbarBtn
            type="button"
            onClick={markAll}
            disabled={selectedHere === total}
            data-testid={`mark-all-${module}`}
          >
            Marcar todos
          </ToolbarBtn>
          <ToolbarBtn
            type="button"
            onClick={clearAll}
            disabled={selectedHere === 0}
            data-testid={`clear-all-${module}`}
          >
            Limpar
          </ToolbarBtn>
        </Toolbar>
      )}
      <ItemList>
        {permissions.map((p) => (
          <Row key={p.id}>
            <input
              type="checkbox"
              checked={selected.has(p.key)}
              disabled={disabled}
              onChange={() => toggle(p.key)}
              data-testid={`perm-${p.key}`}
            />
            <RowText>
              <strong>{p.key}</strong>
              {p.description && <small>{p.description}</small>}
            </RowText>
          </Row>
        ))}
      </ItemList>
    </Card>
  );
};
